<template>
  <div class="release-permissions-config flex column gap20" v-if="organization">
    <h3 class="section-title">{{$t('releaseLocales.permissionsAnd2FA')}}</h3>
    
    <!-- Encrypted Videos Section Permissions -->
    <div v-if="organization.features?.encryptedVideosSection" class="permissions-section flex column gap15">
      <h4>{{$t('releaseLocales.encryptedVideosSection')}}</h4>
      
      <!-- Section-level 2FA Configuration -->
      <div class="flex column gap10">
        <FormInput 
          type="checkbox" 
          :labelholder="'releaseLocales.require2FAForEncryptedVideos'"
          :value="isEncryptedVideos2FAEnabled"
          :disabled="isReleaseLevel2FAEnabled"
          @input="val => updateEncryptedVideos2FA(val)"
        />
        <p v-if="isReleaseLevel2FAEnabled" class="helper-text" style="font-size: 0.85em; color: var(--clr-0-d-40);">
          {{$t('releaseLocales.releaseLevel2FAIncludesAllSections')}}
        </p>
      </div>
      
      <!-- User Permissions for Encrypted Videos Section -->
      <!-- If encryptedVideosSection is enabled, permissions are always available for it -->
      <div class="flex column gap10">
        <p>{{$t('releaseLocales.allowedUsersForEncryptedVideos')}}</p>
        <p class="helper-text">{{$t('releaseLocales.allowedUsersHelper')}}</p>
        <UserPicker
          :value="encryptedVideosAllowedUsers"
          :organizationId="organization._id"
          @input="val => updateEncryptedVideosUsers(val)"
        />
      </div>
    </div>
    
    <!-- Release-level 2FA Configuration -->
    <div v-if="organization.features?.releaseLevel2FA" class="permissions-section flex column gap15">
      <h4>{{$t('releaseLocales.releaseLevel2FA')}}</h4>
      <FormInput 
        type="checkbox" 
        :labelholder="'releaseLocales.require2FAForRelease'"
        :value="isReleaseLevel2FAEnabled"
        :disabled="isEncryptedVideos2FAEnabled"
        @input="val => updateReleaseLevel2FA(val)"
      />
      <p v-if="isEncryptedVideos2FAEnabled" class="helper-text" style="font-size: 0.85em; color: var(--clr-0-d-40);">
        {{$t('releaseLocales.sectionLevel2FAIsMoreSpecific')}}
      </p>
    </div>
  </div>
</template>

<script>
import FormInput from '@/apps/common/modules/common/cmps/FormInput.vue'
import UserPicker from './UserPicker.vue'

export default {
  name: 'ReleasePermissionsConfig',
  components: {
    FormInput,
    UserPicker
  },
  props: {
    release: {
      type: Object,
      required: true
    },
    organization: {
      type: Object,
      required: true
    }
  },
  computed: {
    isEncryptedVideos2FAEnabled() {
      if (!this.release?.require2FAConfig) return false
      if (this.release.require2FAConfig.level !== 'section') return false
      const sectionConfig = this.release.require2FAConfig.sections?.find(
        s => s.sectionName === 'encryptedVideos'
      )
      return sectionConfig?.require2FA === true
    },
    
    encryptedVideosAllowedUsers() {
      if (!this.release?.permissions?.sections) return []
      const sectionPerms = this.release.permissions.sections.find(
        s => s.sectionName === 'encryptedVideos'
      )
      return sectionPerms?.allowedUserIds || []
    },
    
    isReleaseLevel2FAEnabled() {
      if (!this.release?.require2FAConfig) return false
      return this.release.require2FAConfig.level === 'release'
    }
  },
  methods: {
    updateEncryptedVideos2FA(enabled) {
      // Prevent action if release-level 2FA is enabled
      if (this.isReleaseLevel2FAEnabled) {
        return
      }
      
      const updatedData = {
        require2FAConfig: { ...this.release.require2FAConfig }
      }
      
      // Initialize sections array if it doesn't exist
      if (!updatedData.require2FAConfig) {
        updatedData.require2FAConfig = {}
      }
      if (!updatedData.require2FAConfig.sections) {
        updatedData.require2FAConfig.sections = []
      }
      
      if (enabled) {
        // Section-level 2FA: Set level to 'section' and clear any release-level config
        updatedData.require2FAConfig.level = 'section'
        const existingSection = updatedData.require2FAConfig.sections.find(
          s => s.sectionName === 'encryptedVideos'
        )
        if (existingSection) {
          existingSection.require2FA = true
        } else {
          updatedData.require2FAConfig.sections.push({
            sectionName: 'encryptedVideos',
            require2FA: true
          })
        }
      } else {
        // Remove section 2FA config
        updatedData.require2FAConfig.sections = updatedData.require2FAConfig.sections.filter(
          s => s.sectionName !== 'encryptedVideos'
        )
        // If no sections left, set level to 'none' or remove config
        if (updatedData.require2FAConfig.sections.length === 0) {
          updatedData.require2FAConfig.level = 'none'
        }
      }
      
      this.$emit('update', updatedData)
    },
    
    updateEncryptedVideosUsers(userIds) {
      const updatedData = {
        permissions: { ...this.release.permissions }
      }
      
      if (!updatedData.permissions.sections) {
        updatedData.permissions.sections = []
      }
      
      const existingSection = updatedData.permissions.sections.find(
        s => s.sectionName === 'encryptedVideos'
      )
      
      if (userIds && userIds.length > 0) {
        if (existingSection) {
          existingSection.allowedUserIds = userIds
        } else {
          updatedData.permissions.sections.push({
            sectionName: 'encryptedVideos',
            allowedUserIds: userIds,
            allowedRoles: []
          })
        }
      } else {
        // Remove section if no users
        updatedData.permissions.sections = updatedData.permissions.sections.filter(
          s => s.sectionName !== 'encryptedVideos'
        )
      }
      
      this.$emit('update', updatedData)
    },
    
    updateReleaseLevel2FA(enabled) {
      // Prevent action if section-level 2FA is enabled
      if (this.isEncryptedVideos2FAEnabled && !enabled) {
        return
      }
      
      const updatedData = {
        require2FAConfig: { ...this.release.require2FAConfig }
      }
      
      // Initialize require2FAConfig if it doesn't exist
      if (!updatedData.require2FAConfig) {
        updatedData.require2FAConfig = {}
      }
      
      if (enabled) {
        // Release-level 2FA: Set level to 'release' and clear any section-level config
        updatedData.require2FAConfig.level = 'release'
        // Clear section-level configs since release-level covers everything
        if (updatedData.require2FAConfig.sections) {
          updatedData.require2FAConfig.sections = []
        }
      } else {
        updatedData.require2FAConfig.level = 'none'
      }
      
      this.$emit('update', updatedData)
    }
  }
}
</script>

<style lang="scss" scoped>
.release-permissions-config {
  padding: 1em;
  border: 1px solid var(--clr-4-l-80);
  border-radius: 4px;
  background-color: var(--clr-4-l-95);
  
  .section-title {
    font-size: 1.2em;
    font-weight: bold;
    margin-bottom: 0.5em;
  }
  
  .permissions-section {
    padding: 1em;
    background-color: white;
    border-radius: 4px;
    
    h4 {
      font-size: 1.1em;
      font-weight: 600;
      margin-bottom: 0.5em;
    }
    
    .helper-text {
      font-size: 0.9em;
      color: var(--clr-0-d-40);
      font-style: italic;
    }
  }
}
</style>

